import {
  CurecallButton,
  CurecallInput,
  CurecallPhoneInput,
  CurecallSelect,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { ChangeEvent } from 'react'
import { USER_TITLES } from 'src/types/user'
import EmailField from 'src/components/email-field/email-field.component'
import { TMedicalStaffMember } from 'src/types/medical-staff-member'
import { MedicalStaffMemberService } from 'src/services/medical-staff-member.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useQueryClient } from '@tanstack/react-query'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'
import { formatPhoneToInternational } from 'src/utils/phone.util'

export type Props = {
  onCancel: () => void
  selectedMedicalStaffMember?: TMedicalStaffMember
}

export type TMedicalStaffMemberFormValues = {
  civility: TMedicalStaffMember['civility']
  firstname: TMedicalStaffMember['firstname']
  lastname: TMedicalStaffMember['lastname']
  email: TMedicalStaffMember['email']
  phone: TMedicalStaffMember['phone']
  rpps: TMedicalStaffMember['rpps']
}

export default function MedicalStaffMemberFormComponent({
  onCancel,
  selectedMedicalStaffMember,
}: Readonly<Props>) {
  const { project, updateMedicalStaffMembers } = useCurrentProject()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { notify } = useNotification()

  const methods = useForm<TMedicalStaffMemberFormValues>({
    defaultValues: {
      civility: selectedMedicalStaffMember?.civility,
      firstname: selectedMedicalStaffMember?.firstname,
      lastname: selectedMedicalStaffMember?.lastname,
      email: selectedMedicalStaffMember?.email,
      phone: formatPhoneToInternational(selectedMedicalStaffMember?.phone),
      rpps: selectedMedicalStaffMember?.rpps,
    },
  })

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    clearErrors,
    watch,
  } = methods

  const onSubmit: SubmitHandler<TMedicalStaffMemberFormValues> = async (
    values: TMedicalStaffMemberFormValues,
  ) => {
    if (!values) return
    const medicalStaffMember = {
      civility: values.civility,
      firstname: values.firstname,
      lastname: values.lastname,
      phone: values?.phone?.trim() || null,
      email: values.email?.trim() || null,
      rpps: values.rpps,
      professionalCode: selectedMedicalStaffMember?.professionalCode || '10',
      professionalLabel: 'Médecin',
    } as TMedicalStaffMember

    try {
      if (!selectedMedicalStaffMember) {
        const response = await MedicalStaffMemberService.createMedicalStaffMember(
          project.id,
          medicalStaffMember,
        )
        queryClient.setQueryData(
          ['medical-staff-member-list'],
          (oldData: Array<TMedicalStaffMember> | undefined) => {
            return [response, ...(oldData || [])]
          },
        )
        updateMedicalStaffMembers(response)
      } else {
        const updatedMedicalStaffMember = await MedicalStaffMemberService.updateMedicalStaffMember(
          project.id,
          selectedMedicalStaffMember.id,
          medicalStaffMember,
        )
        queryClient.setQueryData(
          ['medical-staff-member-list'],
          (oldData: Array<TMedicalStaffMember> | undefined) => {
            return oldData?.map((staffMember) => {
              return staffMember.id === updatedMedicalStaffMember.id ? updatedMedicalStaffMember : staffMember
            })
          },
        )
        updateMedicalStaffMembers(updatedMedicalStaffMember)
      }
      onCancel()
    } catch (e: any) {
      notify({
        title: t('common.actions.error'),
        message: e,
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        action="#"
        method="POST"
        onSubmit={handleSubmit(onSubmit)}
        className="c-flex c-flex-col c-gap-4 c-pb-8"
      >
        <div className="c-flex c-flex-col c-gap-2">
          <div className="c-flex c-w-full [&>*]:c-w-[100%] c-gap-8 c-mb-5">
            <CurecallSelect
              {...register('civility', { required: t('pages.users-page.form-input-error') })}
              isRequired={true}
              buttonClassName="tw-text-base"
              label={t('pages.users-page.form-user-title-input')}
              placeholder={t('pages.users-page.form-user-title-input')}
              items={USER_TITLES.map((title) => ({ label: title, value: title }))}
              selectedItem={{
                label: watch('civility') as (typeof USER_TITLES)[number],
                value: watch('civility'),
              }}
              errorMessage={errors?.civility && t('pages.users-page.form-input-error')}
              onChange={(item: { label: any; value: (typeof USER_TITLES)[number] }) => {
                clearErrors('civility')
                setValue('civility', item.value)
              }}
            />
            <CurecallInput
              {...register('rpps')}
              label={t('pages.users-page.form-rpps-input')}
              placeholder={t('pages.users-page.form-rpps-input')}
              errorMessage={errors?.rpps && t('pages.users-page.form-input-error')}
              onChange={(item: ChangeEvent<HTMLInputElement>) => {
                clearErrors('rpps')
                setValue('rpps', item.target.value)
              }}
            />
            <CurecallInput
              {...register('firstname', { required: t('pages.users-page.form-input-error') })}
              isRequired={true}
              label={t('pages.users-page.form-firstname-input')}
              placeholder={t('pages.users-page.form-firstname-input')}
              errorMessage={errors?.firstname && t('pages.users-page.form-input-error')}
              onChange={(item: ChangeEvent<HTMLInputElement>) => {
                clearErrors('firstname')
                setValue('firstname', item.target.value)
              }}
            />
            <CurecallInput
              {...register('lastname', { required: t('pages.users-page.form-input-error') })}
              isRequired={true}
              label={t('pages.users-page.form-lastname-input')}
              placeholder={t('pages.users-page.form-lastname-input')}
              errorMessage={errors?.lastname && t('pages.users-page.form-input-error')}
              onChange={(item: ChangeEvent<HTMLInputElement>) => {
                clearErrors('lastname')
                setValue('lastname', item.target.value)
              }}
            />
          </div>
          <div className="c-flex c-w-full [&>*]:c-w-[100%] c-gap-8">
            <EmailField name="email" />
            <CurecallPhoneInput
              {...register('phone')}
              label={t('pages.users-page.form-phone-input')}
              placeholder={t('pages.users-page.form-phone-input')}
              notValidPhoneErrorMessage={t('pages.users-page.form-phone-invalid-error')}
              notCompletePhoneNumberErrorMessage={t('pages.users-page.form-phone-incomplete-error')}
              errorMessage={errors?.phone && t('pages.users-page.form-input-error')}
              tooltipContent={t('pages.users-page.tooltip-phone')}
              onChange={(phone) => {
                clearErrors('phone')
                setValue('phone', phone)
              }}
            />
          </div>
          <div className="c-w-[30%]"></div>
        </div>
        <div className="c-flex c-justify-center c-gap-4 c-mt-8">
          <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onCancel}>
            {t('pages.users-page.cta-cancel')}
          </CurecallButton>
          <CurecallButton appearance={ECurecallButtonAppearance.SECONDARY} type="submit">
            {selectedMedicalStaffMember
              ? t('pages.users-page.cta-medical-staff-member-update')
              : t('pages.users-page.cta-medical-staff-member-add')}
          </CurecallButton>
        </div>
      </form>
    </FormProvider>
  )
}
